
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EnrolledUser } from "@/types/attendance";
import { useNotifications } from "@/hooks/useNotifications";

interface AttendanceUpdateParams {
  classId: string | undefined;
  userId: string;
  attended: boolean;
  penalized: boolean;
  selectedDate: string;
}

export const useAttendanceMutation = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();

  return useMutation({
    mutationFn: async (params: AttendanceUpdateParams) => {
      if (!params.classId) throw new Error("ID de clase no proporcionado");
      
      // Obtenemos primero los datos de usuarios inscritos
      const enrolledUsersData = queryClient.getQueryData<EnrolledUser[]>(['enrolled-users', params.classId, params.selectedDate]);
      const userRecord = enrolledUsersData?.find(user => user.user_id === params.userId);
      
      if (userRecord?.attendanceRecord) {
        // Actualizar registro existente
        const { error } = await supabase
          .rpc('update_class_attendance', {
            p_id: userRecord.attendanceRecord.id,
            p_attended: params.attended,
            p_penalized: params.penalized
          });
        
        if (error) throw error;
      } else {
        // Crear nuevo registro
        const { error } = await supabase
          .rpc('create_class_attendance', {
            p_class_id: params.classId,
            p_user_id: params.userId,
            p_attended: params.attended,
            p_penalized: params.penalized,
            p_date: params.selectedDate
          });
        
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrolled-users', variables.classId, variables.selectedDate] });
      
      success("Registro actualizado", {
        description: "Se ha actualizado el registro de asistencia"
      });
    },
    onError: (error) => {
      console.error('Error al actualizar asistencia:', error);
      
      showError("No se pudo actualizar el registro de asistencia");
    },
  });
};
